import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import { z } from "zod";
import {
    agendaPresetIdFactory,
    agendaPresetIdZodSchema,
} from "./agenda-preset-id";
import { agendaPreset_dynamicTimeZodEnum } from "./agenda-preset.dynamic-time";
import { agendaFilterZodObject } from "./agenda-preset.filters";
import { agendaPreset_viewOptionsZodObject } from "./agenda-preset.view-options";

/**
 * The schema for an agenda preset.
 * An agenda preset is a set of filters and settings that can be applied to an agenda to
 * filter the items displayed in the agenda.
 *
 * Properties:
 * - `_id`: A unique identifier for the agenda preset, generated by `agendaPresetIdFactory`.
 * - `_createdAt`: The creation timestamp in ISO and Unix format, generated by `nowIsoAndUnix`.
 * - `name`: A required translatable name for the agenda preset.
 * - `providerId`: The identifier for the provider.
 * - `userId`: An optional identifier for the user.
 *
 * Filters:
 * - `filters`: An array of filters for the agenda preset.
 * - `dynamicTime`: The dynamic time for the agenda preset.
 *
 * Settings:
 * - `shared`: A boolean indicating if the preset is shared, defaults to true.
 * - `viewOptions`: The view options for the agenda preset.
 */
const agendaPresetZodObject = z.object({
    _id: agendaPresetIdZodSchema.default(() => agendaPresetIdFactory()),
    _createdAt: isoAndUnixZodSchema.default(() => nowIsoAndUnix()),
    _type: z.literal("AGENDA_PRESET").default("AGENDA_PRESET"),
    name: z.string().min(1),
    providerId: providerIdZodSchema,
    userId: userIdZodSchema.optional(),

    // Filters
    filters: z.array(agendaFilterZodObject).default([]),
    dynamicTime: agendaPreset_dynamicTimeZodEnum.default("today").optional(),

    // Settings
    shared: z.boolean().default(true),
    viewOptions: agendaPreset_viewOptionsZodObject.optional(),
});

/**
 * An agenda preset is a set of filters and settings that can be applied to an agenda to
 * filter the items displayed in the agenda.
 **/
type AgendaPreset = z.infer<typeof agendaPresetZodObject>;

const agendaPresetFactoryParamsZodObject = agendaPresetZodObject.omit({
    _id: true,
    _createdAt: true,
    _type: true,
});
type AgendaPresetFactoryParams = z.infer<
    typeof agendaPresetFactoryParamsZodObject
>;

/**
 * Factory function to create an agenda preset.
 *
 * @param params - The parameters for the agenda preset.
 * @returns The parsed agenda preset object.
 */
const agendaPresetFactory = (params: AgendaPresetFactoryParams) =>
    agendaPresetZodObject.parse(params);

export {
    agendaPresetFactory,
    agendaPresetFactoryParamsZodObject,
    agendaPresetZodObject,
};
export type { AgendaPreset, AgendaPresetFactoryParams };
